import React from "react"
import { faClose, faFileLines, faPrint, faReceipt } from "@fortawesome/free-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { useTranslation } from "react-i18next"
import Animate, { AnimationTypes } from "../../components/Animate/Animate"
import { ButtonIcon } from "../../components/Button/Button"
import ModalPortal from "../../components/Modal/ModalPortal"
import { TitlePageResponsiveIconSmall } from "../../components/Titles/Titles"
import THEME from "../../configuration/Theme"
import { SIZE } from "../../configuration/Sizes"
import defaultStyle from "../../styles/defaultPageStyle.module.css"

export type CashFlowPrintFormat = "a4" | "thermal"

interface CashFlowPrintFormatPopupProps {
  onSelectFormat: (format: CashFlowPrintFormat) => void
  onClose: () => void
}

export const CashFlowPrintFormatPopup: React.FC<CashFlowPrintFormatPopupProps> = ({ onSelectFormat, onClose }) => {
  const { t } = useTranslation("pages")

  const optionClassName =
    "flex w-full items-start gap-3 rounded-xl border-2 border-slate-200 bg-white p-4 text-left shadow-sm transition hover:border-orange-400 hover:bg-orange-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-500 focus-visible:ring-offset-2"

  return (
    <ModalPortal>
      <div className={defaultStyle.overlay}>
        <Animate type={AnimationTypes.slideUpIn} duration="0.3s">
          <div className={defaultStyle.alert1}>
            <TitlePageResponsiveIconSmall title={t("cashflow.print_format_popup.title")} icon={faPrint}>
              <div className={defaultStyle.ButtonsActions}>
                <ButtonIcon
                  sizeNumber={SIZE.buttonCloseIconSize}
                  bgColor={THEME.colors.buttonBGCloseColor}
                  icon={faClose}
                  typeButton="button"
                  onClick={onClose}
                  size="small"
                  descriptionKey="interactionDescriptions:pages.cashFlow.cashFlowPrintFormatPopup.button.onClose"
                />
              </div>
            </TitlePageResponsiveIconSmall>

            <Animate type={AnimationTypes.fadeInTraslate} duration="1.5s">
              <div className="flex flex-col gap-3 p-4">
                <p className="text-sm leading-relaxed text-slate-600">{t("cashflow.print_format_popup.description")}</p>

                <button
                  type="button"
                  className={optionClassName}
                  onClick={() => onSelectFormat("a4")}
                  data-interaction-description-key="interactionDescriptions:pages.cashFlow.cashFlowPrintFormatPopup.button.a4"
                >
                  <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-orange-100 text-orange-600">
                    <FontAwesomeIcon icon={faFileLines} />
                  </span>

                  <span className="min-w-0">
                    <span className="block text-sm font-extrabold text-slate-900">
                      {t("cashflow.print_format_popup.a4_title")}
                    </span>

                    <span className="mt-1 block text-xs font-medium leading-5 text-slate-600">
                      {t("cashflow.print_format_popup.a4_description")}
                    </span>
                  </span>
                </button>

                <button
                  type="button"
                  className={optionClassName}
                  onClick={() => onSelectFormat("thermal")}
                  data-interaction-description-key="interactionDescriptions:pages.cashFlow.cashFlowPrintFormatPopup.button.thermal"
                >
                  <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-emerald-100 text-emerald-600">
                    <FontAwesomeIcon icon={faReceipt} />
                  </span>

                  <span className="min-w-0">
                    <span className="block text-sm font-extrabold text-slate-900">
                      {t("cashflow.print_format_popup.thermal_title")}
                    </span>

                    <span className="mt-1 block text-xs font-medium leading-5 text-slate-600">
                      {t("cashflow.print_format_popup.thermal_description")}
                    </span>
                  </span>
                </button>
              </div>
            </Animate>
          </div>
        </Animate>
      </div>
    </ModalPortal>
  )
}
